import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/pages/home/components/Navbar';
import Footer from '@/pages/home/components/Footer';
import { SITE_URL } from '@/constants/site';

interface ArticleLayoutProps {
  title: string;
  description: string;
  tag: string;
  slug: string;
  datePublished?: string;
  dateModified?: string;
  faqItems?: { question: string; answer: string }[];
  children: React.ReactNode;
}

export default function ArticleLayout({
  title,
  description,
  tag,
  slug,
  datePublished = '2026-07-01',
  dateModified = '2026-07-14',
  faqItems = [],
  children,
}: ArticleLayoutProps) {
  const canonicalUrl = `${SITE_URL}/articoli/${slug}`;

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'auto' });
  }, []);

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: title,
    description,
    author: {
      '@type': 'Person',
      name: 'Marco Rossi',
      jobTitle: 'Esperto di shopping internazionale',
    },
    datePublished,
    dateModified,
    publisher: {
      '@type': 'Organization',
      name: 'Itaobuy Spread',
      url: SITE_URL,
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': canonicalUrl,
    },
    inLanguage: 'it',
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Itaobuy Spread',
        item: SITE_URL,
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: title,
        item: canonicalUrl,
      },
    ],
  };

  const faqSchema = faqItems.length > 0 ? {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqItems.map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.answer,
      },
    })),
  } : null;

  return (
    <>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}></script>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}></script>
      {faqSchema && (
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}></script>
      )}
      <Navbar />
      <main>
        <article>
          <header className="pt-24 pb-12 md:pt-32 md:pb-16 bg-background-50">
            <div className="max-w-[860px] mx-auto px-6 md:px-10">
              <Link
                to="/"
                className="inline-flex items-center gap-1.5 text-sm text-primary-600 hover:text-primary-700 font-medium mb-6 cursor-pointer"
              >
                <i className="ri-arrow-left-line"></i>
                Torna a Itaobuy Spread
              </Link>

              <span className="inline-block text-xs tracking-[0.2em] uppercase text-primary-600 font-label mb-3 px-3 py-1 rounded-full bg-primary-50">
                {tag}
              </span>

              <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-900 leading-tight mb-4">
                {title}
              </h1>

              <p className="text-foreground-600 text-base md:text-lg leading-relaxed mb-6 max-w-[700px]">
                {description}
              </p>

              <div className="flex items-center gap-4 text-sm text-foreground-500">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-secondary-200 flex items-center justify-center">
                    <span className="text-xs font-semibold text-secondary-700">MR</span>
                  </div>
                  <div>
                    <p className="font-medium text-foreground-800">Marco Rossi</p>
                    <p className="text-xs">Esperto di shopping internazionale</p>
                  </div>
                </div>
                <span className="text-foreground-300">|</span>
                <span>Ultimo aggiornamento: Luglio 2026</span>
              </div>
            </div>
          </header>

          <div className="max-w-[860px] mx-auto px-6 md:px-10 py-10 md:py-14">
            {children}

            <div className="flex flex-col sm:flex-row items-center gap-4 mt-16 pt-10 border-t border-background-200/70">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full bg-primary-500 text-white font-medium text-sm hover:bg-primary-600 transition-all whitespace-nowrap cursor-pointer"
              >
                itaobuy sheet
                <i className="ri-arrow-right-up-line"></i>
              </a>
              <Link
                to="/"
                className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full bg-background-100 border border-background-300 text-foreground-700 font-medium text-sm hover:bg-background-200 transition-all whitespace-nowrap cursor-pointer"
              >
                Torna alla Home
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>

            <div className="bg-background-100 rounded-xl p-6 flex flex-col sm:flex-row gap-4 items-start border border-background-200/70 mt-10">
              <div className="w-14 h-14 rounded-full bg-secondary-200 flex items-center justify-center flex-shrink-0">
                <span className="text-lg font-bold text-secondary-700">MR</span>
              </div>
              <div>
                <p className="font-heading text-base font-semibold text-foreground-900 mb-1">Scritto da Marco Rossi</p>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  Esperto di shopping internazionale con oltre 5 anni di esperienza nel settore.
                  Aiuto gli utenti italiani a trovare i migliori prodotti online attraverso guide
                  dettagliate, analisi comparative e consigli pratici basati sull&apos;esperienza diretta.
                </p>
              </div>
            </div>
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}